/**
 * Frame cluster (#421) — the top-of-screen party/target frame group.
 *
 * Before #421, `OwnFrame`/`TargetFrame`/`FriendlyTargetFrame` each
 * self-positioned with a hardcoded `left: 20%/40%/60%` — a coincidental
 * left-to-right ordering that didn't actually GROUP friendly vs. enemy (and
 * would silently drift the moment a frame's width changed). `UnitFrameStack`
 * (#138) already established the correct convention for the bottom roster:
 * one flex container (`display: flex`, `justifyContent: space-between`)
 * with a friendly group anchored left and an enemy group anchored right.
 *
 * This component extends that SAME convention to the top cluster:
 *   • friendly (LEFT): `OwnFrame` (self) + `FriendlyTargetFrame` (ally
 *     target, `targets.friendly`) — both are "the viewer's side."
 *   • enemy (RIGHT): `TargetFrame` (hostile target, `targets.hostile`).
 *
 * Each frame keeps its own component/testid contract untouched — only the
 * OUTER positioning moved here (the three frames no longer carry `position:
 * absolute`/`top`/`left` themselves).
 *
 * PURELY COSMETIC / CLIENT-SIDE layout. No sim, no protocol, no hash.
 */

import type { CSSProperties, ReactElement } from "react";
import { OwnFrame } from "./OwnFrame.js";
import { FriendlyTargetFrame } from "./FriendlyTargetFrame.js";
import { TargetFrame } from "./TargetFrame.js";

const GROUP_STYLE: CSSProperties = {
  display: "flex",
  flexDirection: "row",
  gap: 12,
  pointerEvents: "none",
};

export function FrameCluster(): ReactElement {
  return (
    <div
      data-testid="frame-cluster"
      style={{
        position: "absolute",
        top: 16,
        left: 0,
        right: 0,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        padding: "0 12px",
        pointerEvents: "none",
      }}
    >
      <div data-testid="frame-cluster-friendly" style={GROUP_STYLE}>
        <OwnFrame />
        <FriendlyTargetFrame />
      </div>
      <div data-testid="frame-cluster-enemy" style={GROUP_STYLE}>
        <TargetFrame />
      </div>
    </div>
  );
}
